import './TransactionFeesTable.scss';
import React from 'react';
import { FormGroup, Col, Row } from 'reactstrap';
import { Field, Input } from '@availity/form';
import InfoTooltip from '../../../../../components/info-tooltip/info-tooltip';
import { MarketplacePercentField } from '../MarketplaceSettingsFields/MarketplacePercentField';
import { MarketplaceCurrencyField } from '../MarketplaceSettingsFields/MarketplaceCurrencyField';
import {
    FeeRefundStatus,
    FeeRefundStatuses,
} from '../../../../../slices/marketplace/pricing/models/fee-refund-status.enum';
import { TransactionFees } from '../../../../../slices/marketplace/pricing/models/transaction-fees.model';
import { MarketplacePricing } from '../../../../../slices/marketplace/pricing/models/marketplace-pricing.model';

interface TransactionFeesTableProps {
    brokerageType: 'discountBrokeragePricing' | 'fullServiceBrokeragePricing';
    transactionFees: TransactionFees;
    onChange: (newState: TransactionFees) => void;
    setFormFieldValue: (name: string, value: string) => void;
    formState: MarketplacePricing;
}

const TransactionFeesTable = (props: TransactionFeesTableProps) => {
    const labelFieldWidthMd = 2;
    const multiFieldWidthMd = 5;
    const refundableFieldWidthMd = 2;
    const creditableFieldWidthMd = 2;
    const partiallyRefundableTooltipText =
        'The application fee will be partially refunded in each of the following circumstances: ' +
        '1. 50% refund if a completed process yeilds no LOIs, ' +
        '2. 25% refund if a completed process yeilds ONLY 1 LOI, ' +
        "3. Up to 50% refund, at VetValue's discretion, depending upon the circumstances, if the engagement application is denied by VetValue";

    const refundableField = (fieldId: keyof TransactionFees) => (
        <>
            <div
                className={`shorter-field ${
                    props.transactionFees[fieldId] == FeeRefundStatus.Partially
                        ? ''
                        : 'info-tooltip-offset'
                }`}>
                <Input
                    type="select"
                    name={`${props.brokerageType}.transactionFees.${fieldId}`}
                    value={props.transactionFees[fieldId]}
                    onChange={(event) => {
                        const numVal = Number(event.currentTarget.value);
                        const newState = {
                            ...props.transactionFees,
                        };
                        newState[fieldId] = numVal;
                        props.onChange(newState);
                    }}>
                    {FeeRefundStatuses.map((item) => (
                        <option
                            value={item.id}
                            key={props.brokerageType + fieldId + item.id}>
                            {item.name}
                        </option>
                    ))}{' '}
                </Input>
            </div>
            {props.transactionFees[fieldId] == FeeRefundStatus.Partially ? (
                <InfoTooltip
                    idText={`${props.brokerageType}.transactionFees.${fieldId}`}
                    text={partiallyRefundableTooltipText}
                />
            ) : (
                []
            )}
        </>
    );

    const creditableField = (fieldId: keyof TransactionFees) => (
        <div className="shorter-field">
            <MarketplacePercentField
                name={`${props.brokerageType}.transactionFees.${fieldId}`}
                value={
                    props.formState[props.brokerageType].transactionFees[
                        fieldId
                    ]
                }
                setValue={(name: string, value: string): void => {
                    props.setFormFieldValue(name, value);
                    const newState = {
                        ...props.transactionFees,
                    };
                    newState[fieldId] = Number(value.replace('%', ''));
                    props.onChange(newState);
                }}
            />
        </div>
    );

    const validateMonthsField = (event: any, keyprop: any, prefix: any) => {
        if (
            !/[0-9]/.test(event.key) &&
            event.key != 'Backspace' &&
            event.key != 'ArrowRight' &&
            event.key != 'ArrowLeft' &&
            event.key != 'Tab'
        ) {
            return true;
        }
        return false;
    };

    return (
        <div className="transaction-fees-table">
            <div className="fee-section">
                <FormGroup>
                    <div className="fee-section">
                        <Row className="center-items fee-item-row margin-bottom-1-rem d-none d-md-flex">
                            <Col md={labelFieldWidthMd}>
                                <label>Fee Type</label>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="conditional-text-center">
                                <label>Fee Amount</label>
                            </Col>
                            <Col
                                md={refundableFieldWidthMd}
                                className="conditional-text-center aligned-md-2">
                                <label className="refundable-label">
                                    Refundable
                                </label>
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className="conditional-text-center aligned-md-2">
                                <label>Creditable</label>
                                <InfoTooltip
                                    idText={`${props.brokerageType}-creditable-header`}
                                    text={
                                        'Creditable against the transaction fee'
                                    }
                                />
                            </Col>
                        </Row>
                    </div>
                    <div className="fee-section">
                        <Row className="center-items fee-item-row margin-bottom-1-rem">
                            <Col md={labelFieldWidthMd}>
                                {' '}
                                <div className="label-with-tooltip">
                                    <label>Buyer Meeting Credits</label>
                                    <InfoTooltip
                                        idText={`${props.brokerageType}.transactionFees.buyerMeetingCredit`}
                                        text={
                                            'Buyers are charged a refundable meeting fee for either an in-person or virtual meeting with the seller. ' +
                                            'This fee is refunded to the buyer if the seller is unable or unwilling to attend the meeting. ' +
                                            'Meetings are typically 3-5 hours in length and may include discussion over a meal. ' +
                                            "There is no meeting fee for the seller. VetValue credits 50% of the buyer's meeting fee towards the seller's transaction fee. "
                                        }
                                    />
                                </div>
                                <label>(NOT a Fee)</label>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="right-padding-5">
                                <label className="d-md-none transaction-fees-cell-label">
                                    Fee Amount
                                </label>
                                <Col className="multi-field left-padding-0 margin-bottom-half-rem">
                                    <div className="shorter-field">
                                        <MarketplaceCurrencyField
                                            name={`${props.brokerageType}.transactionFees.buyerMeetingVirtualCredit`}
                                            value={
                                                props.formState[
                                                    props.brokerageType
                                                ].transactionFees
                                                    .buyerMeetingVirtualCredit
                                            }
                                            setFormValue={(
                                                name: string,
                                                value: string
                                            ): void => {
                                                props.setFormFieldValue(
                                                    name,
                                                    value
                                                );
                                                const newState = {
                                                    ...props.transactionFees,
                                                };
                                                newState.buyerMeetingVirtualCredit =
                                                    Number(value);
                                                props.onChange({
                                                    ...newState,
                                                });
                                            }}
                                        />
                                    </div>

                                    <label className="label-right">
                                        credit per virtual meeting
                                    </label>
                                </Col>
                                <Col className="multi-field left-padding-0">
                                    <div className="shorter-field">
                                        <MarketplaceCurrencyField
                                            name={`${props.brokerageType}.transactionFees.buyerMeetingInPersonCredit`}
                                            value={
                                                props.formState[
                                                    props.brokerageType
                                                ].transactionFees
                                                    .buyerMeetingInPersonCredit
                                            }
                                            setFormValue={(
                                                name: string,
                                                value: string
                                            ): void => {
                                                props.setFormFieldValue(
                                                    name,
                                                    value
                                                );
                                                const newState = {
                                                    ...props.transactionFees,
                                                };
                                                newState.buyerMeetingInPersonCredit =
                                                    Number(value);
                                                props.onChange({
                                                    ...newState,
                                                });
                                            }}
                                        />
                                    </div>

                                    <label className="label-right">
                                        credit per in-person meeting
                                    </label>
                                </Col>
                            </Col>

                            <Col
                                md={refundableFieldWidthMd}
                                className="conditional-text-center transaction-fees-refundable-cell aligned-md-2">
                                <label className="d-md-none transaction-fees-cell-label">
                                    Refundable
                                </label>
                                <div className="multi-field flex-hor-center">
                                    {refundableField('buyerMeetingRefundable')}
                                </div>
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className="conditional-text-center transaction-fees-creditable-cell aligned-md-2">
                                <label className="d-md-none transaction-fees-cell-label">
                                    Creditable
                                </label>
                                <div className="flex-hor-center">
                                    {creditableField('buyerMeetingCreditable')}
                                </div>
                            </Col>
                        </Row>
                    </div>
                    <div className="fee-section">
                        <Row className="center-items fee-item-row margin-bottom-1-rem">
                            <Col md={labelFieldWidthMd}>
                                <div className="label-with-tooltip">
                                    <label>Application Fee</label>
                                    <InfoTooltip
                                        idText={`${props.brokerageType}.transactionFees.applicationFee`}
                                        text={
                                            'Charged at the time of application to engage this platform'
                                        }
                                    />
                                </div>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="right-padding-5 transaction-fees-amount-cell">
                                <label className="d-md-none transaction-fees-cell-label">
                                    Fee Amount
                                </label>
                                <div className="multi-field">
                                    <div className="shorter-field">
                                        <MarketplaceCurrencyField
                                            name={`${props.brokerageType}.transactionFees.applicationFee`}
                                            value={
                                                props.formState[props.brokerageType]
                                                    .transactionFees.applicationFee
                                            }
                                            setFormValue={(
                                                name: string,
                                                value: string
                                            ): void => {
                                                props.setFormFieldValue(
                                                    name,
                                                    value
                                                );
                                                const newState = {
                                                    ...props.transactionFees,
                                                };
                                                newState.applicationFee =
                                                    value ? Number(value) : 0;
                                                props.onChange({
                                                    ...newState,
                                                });
                                            }}
                                        />
                                    </div>

                                    <label className="label-inner">+</label>

                                    <div className="shorter-field">
                                        <MarketplaceCurrencyField
                                            name={`${props.brokerageType}.transactionFees.applicationFeePerDvm`}
                                            value={
                                                props.formState[props.brokerageType]
                                                    .transactionFees
                                                    .applicationFeePerDvm
                                            }
                                            setFormValue={(
                                                name: string,
                                                value: string
                                            ): void => {
                                                props.setFormFieldValue(
                                                    name,
                                                    value
                                                );
                                                const newState = {
                                                    ...props.transactionFees,
                                                };
                                                newState.applicationFeePerDvm =
                                                    value ? Number(value) : 0;
                                                props.onChange({
                                                    ...newState,
                                                });
                                            }}
                                        />
                                    </div>

                                    <label className="label-right">
                                        per FTE DVM
                                    </label>
                                </div>
                            </Col>
                            <Col
                                md={refundableFieldWidthMd}
                                className="conditional-text-center transaction-fees-refundable-cell aligned-md-2">
                                <label className="d-md-none transaction-fees-cell-label">
                                    Refundable
                                </label>
                                <div className="multi-field flex-hor-center">
                                    {refundableField('applicationFeeRefundable')}
                                </div>
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className="conditional-text-center transaction-fees-creditable-cell aligned-md-2">
                                <label className="d-md-none transaction-fees-cell-label">
                                    Creditable
                                </label>
                                <div className="flex-hor-center">
                                    {creditableField('applicationFeeCreditable')}
                                </div>
                            </Col>
                        </Row>

                    </div>
                </FormGroup>

                <FormGroup>
                    <div className="fee-section">
                        <Row className="center-items fee-item-row">
                            <Col md={labelFieldWidthMd}>
                                <label>Tail Period</label>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="transaction-fees-amount-cell">
                                <label className="d-md-none transaction-fees-cell-label">
                                    Fee Amount
                                </label>
                                <div className="shorter-field">
                                    <Field
                                        type="text"
                                        name={`${props.brokerageType}.transactionFees.tailPeriodMonths`}
                                        value={
                                            (props.transactionFees.tailPeriodMonths.toString() ??
                                                '0') + ' months'
                                        }
                                        min={1}
                                        max={100}
                                        onKeyDown={(event) => {
                                            if (
                                                validateMonthsField(
                                                    event,
                                                    props.formState[
                                                        props.brokerageType
                                                    ].transactionFees
                                                        .tailPeriodMonths,
                                                    ' months'
                                                )
                                            ) {
                                                event.preventDefault();
                                            }
                                        }}
                                        onChange={(event) => {
                                            let value = event.target.value;
                                            let res = value.replace(/\D/g, '');
                                            if (!value.includes(' months')) {
                                                res = res.slice(0, -1);
                                            }
                                            const newState = {
                                                ...props.transactionFees,
                                            };
                                            newState.tailPeriodMonths =
                                                Number(res);
                                            props.onChange({
                                                ...newState,
                                            });
                                            props.setFormFieldValue(
                                                `${props.brokerageType}.transactionFees.tailPeriodMonths`,
                                                res
                                            );
                                        }}></Field>
                                </div>
                            </Col>
                            <Col
                                md={refundableFieldWidthMd}
                                className="conditional-text-center transaction-fees-refundable-cell aligned-md-2">
                                <label className="d-md-none transaction-fees-cell-label">
                                    Refundable
                                </label>
                                <label>NA</label>
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className="conditional-text-center transaction-fees-creditable-cell aligned-md-2">
                                <label className="d-md-none transaction-fees-cell-label">
                                    Creditable
                                </label>
                                <label>NA</label>
                            </Col>
                        </Row>
                    </div>
                </FormGroup>
            </div>
        </div>
    );
};

export default TransactionFeesTable;
